import React from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Spin } from './Spin';
import { Empty } from './Empty';
import styles from './List.module.css';

const list = cva(styles.base, {
  variants: {
    bordered: { true: styles.bordered },
  },
});

interface ListItemMetaProps {
  avatar?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
}

function ListItemMeta({ avatar, title, description }: ListItemMetaProps) {
  return (
    <div className={styles.meta}>
      {avatar && <div className={styles.metaAvatar}>{avatar}</div>}
      <div className={styles.metaText}>
        {title && (
          <div className={styles.metaTitle}>
            {title}
          </div>
        )}
        {description && (
          <div className={styles.metaDescription}>
            {description}
          </div>
        )}
      </div>
    </div>
  );
}

interface ListItemProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick' | 'title'> {
  // Existing data-display props (backward compat)
  actions?: React.ReactNode[];
  extra?: React.ReactNode;
  children?: React.ReactNode;
  // New interactive props
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  hint?: React.ReactNode;
  trailingReveal?: 'hover' | 'always';
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  highlighted?: boolean;
  onHover?: () => void;
  size?: 'sm' | 'md' | 'lg';
  radius?: 'sm' | 'md' | 'lg' | 'full';
  role?: string;
}

const sizeClass: Record<string, string> = {
  sm: styles.itemSm,
  md: styles.itemMd,
  lg: styles.itemLg,
};

const radiusClass: Record<string, string> = {
  sm: styles.itemRadiusSm,
  md: styles.itemRadiusMd,
  lg: styles.itemRadiusLg,
  full: styles.itemRadiusFull,
};

function ListItem({
  // data-display
  actions, extra,
  // interactive
  leading, trailing, hint, trailingReveal = 'always',
  onClick, active = false, disabled = false, highlighted = false,
  onHover, size = 'md', radius = 'md',
  role,
  // shared
  children, className,
  // extract to prevent prop spread from overwriting internal handler
  onKeyDown: onKeyDownProp,
  ...props
}: ListItemProps) {
  const isInteractive = !!(onClick || leading || trailing || highlighted || active || hint || onHover);

  const handleClick = () => {
    if (!onClick || disabled) return;
    onClick();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (onClick && !disabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
    onKeyDownProp?.(e);
  };

  const trailingCls = trailingReveal === 'hover' ? styles.trailingHover : styles.trailingAlways;

  if (isInteractive) {
    return (
      <div
        className={clsx(
          styles.itemInteractive,
          sizeClass[size],
          radiusClass[radius],
          active && styles.active,
          disabled && styles.disabled,
          highlighted && styles.highlighted,
          className
        )}
        role={role ?? (onClick ? 'button' : undefined)}
        tabIndex={onClick ? 0 : undefined}
        aria-disabled={onClick && disabled ? true : undefined}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={onHover}
        {...props}
      >
        {leading && <div className={styles.leading}>{leading}</div>}
        <div className={styles.interactiveBody}>{children}</div>
        {trailing && <div className={clsx(styles.trailing, trailingCls)}>{trailing}</div>}
        {hint && <div className={styles.hint}>{hint}</div>}
      </div>
    );
  }

  // Data display mode (existing behaviour)
  return (
    <div className={clsx(styles.item, className)} {...props}>
      <div className={styles.itemContent}>
        {children}
        {actions && (
          <div className={styles.itemActions}>
            {actions.map((action, i) => (
              <div key={i} className={styles.actionItem}>{action}</div>
            ))}
          </div>
        )}
      </div>
      {extra && <div className={styles.itemExtra}>{extra}</div>}
    </div>
  );
}

interface ListProps<T> extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  dataSource?: T[];
  renderItem?: (item: T, index: number) => React.ReactNode;
  loading?: boolean;
  bordered?: boolean;
  size?: 'default' | 'small' | 'large';
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children?: React.ReactNode;
}

const padMap: Record<string, string> = {
  small: 'var(--space-2)',
  default: 'var(--space-3)',
  large: 'var(--space-5)',
};

/**
 * List — vertical content list.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | dataSource | T[] | — | Data items |
 * | renderItem | (item, index) => ReactNode | — | Render function |
 * | loading | boolean | false | Show spinner |
 * | bordered | boolean | false | Show border |
 * | size | 'default' \| 'small' \| 'large' | 'default' | Padding |
 * | header | ReactNode | — | List header |
 * | footer | ReactNode | — | List footer |
 *
 * Sub: List.Item, List.Item.Meta
 *
 * @example
 * <List
 *   dataSource={notes}
 *   renderItem={(note) => (
 *     <List.Item>
 *       <List.Item.Meta title={note.title} description={note.preview} />
 *     </List.Item>
 *   )}
 * />
 */
export function List<T>({
  dataSource,
  renderItem,
  loading = false,
  bordered = false,
  size = 'default',
  header,
  footer,
  children,
  className,
  style,
  ...props
}: ListProps<T>) {
  const pad = padMap[size];

  const content =
    dataSource && renderItem
      ? dataSource.map((item, i) => <React.Fragment key={i}>{renderItem(item, i)}</React.Fragment>)
      : children;

  const isEmpty = dataSource ? dataSource.length === 0 : !children;

  // Build padding as CSS custom properties
  const headerPad = `${pad} ${bordered ? pad : '0'}`;
  const bodyPad = bordered ? `0 ${pad}` : undefined;
  const footerPad = `${pad} ${bordered ? pad : '0'}`;

  return (
    <div
      className={clsx(list({ bordered }), className)}
      style={style}
      {...props}
    >
      {header && (
        <div
          className={styles.header}
          style={{ '--list-header-padding': headerPad } as React.CSSProperties}
        >
          {header}
        </div>
      )}

      <Spin spinning={loading}>
        <div
          className={styles.body}
          style={bodyPad ? { '--list-body-padding': bodyPad } as React.CSSProperties : undefined}
        >
          {isEmpty && !loading ? <Empty description="No data" /> : content}
        </div>
      </Spin>

      {footer && (
        <div
          className={styles.footer}
          style={{ '--list-footer-padding': footerPad } as React.CSSProperties}
        >
          {footer}
        </div>
      )}
    </div>
  );
}

ListItem.Meta = ListItemMeta;
List.Item = ListItem;
